/**
 * Admin — create notice ("Create notice" mobile frame): priority, audience,
 * title, body, require-acknowledgement toggle.
 * Wired: POST /api/admin/notices (title/kind/body). Port of
 * screens-admin.jsx CreateNotice.
 *
 * DEVIATION: `audience` and `requireAck` are sent as additive fields the
 * backend doesn't yet persist (POST /api/admin/notices only reads
 * title/kind/body/pinned/attachment* — extra keys are silently ignored, not
 * an error) — kept for frame fidelity, flagged TODO(wire) like the OAuth
 * buttons in app/login.tsx.
 */
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { InfoNote, Toggle } from '@/components/gate/bits';
import { AdminScreenShell, goBack } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { BottomBar, Segmented } from '@/features/community/bits';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Field, IconButton, Input, Screen } from '@/ui';

const KINDS = [
  { key: 'general', label: 'General' },
  { key: 'urgent', label: 'Urgent' },
  { key: 'event', label: 'Event' },
  { key: 'committee', label: 'Committee' },
];

const AUDIENCES = [
  { key: 'ALL', label: 'All wings' },
  { key: 'OWNERS', label: 'Owners only' },
  { key: 'TENANTS', label: 'Tenants only' },
];

export default function CreateNoticeScreen() {
  const { colors } = useTheme();
  const [title, setTitle] = React.useState('');
  const [kind, setKind] = React.useState('general');
  const [audience, setAudience] = React.useState('ALL');
  const [requireAck, setRequireAck] = React.useState(false);
  const [body, setBody] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);

  const canPublish = title.trim().length > 0 && body.trim().length > 0;

  const publish = async () => {
    if (!canPublish) return;
    setSaving(true);
    setNotice(null);
    try {
      await api.post<unknown>('/api/admin/notices', {
        title: title.trim(), kind, body: body.trim(),
        // TODO(wire): audience + requireAck aren't persisted yet.
        audience, requireAck,
      });
      goBack();
    } catch {
      setNotice('Could not publish the notice right now. Try again in a bit.');
      setSaving(false);
    }
  };

  return (
    <AdminScreenShell
      title="New notice"
      right={
        <IconButton label="Discard" onPress={goBack}>
          <Icon name="x" size={20} color={colors.textPrimary} />
        </IconButton>
      }
    >
      <Screen padded style={{ paddingTop: space.s4, gap: space.s4 }}>
        <Field label="Title">
          <Input value={title} onChangeText={setTitle} placeholder="Notice title" />
        </Field>
        <Field label="Priority">
          <Segmented options={KINDS} value={kind} onChange={setKind} />
        </Field>
        <Field label="Audience">
          <Segmented options={AUDIENCES} value={audience} onChange={setAudience} />
        </Field>
        <Field label="Body">
          <Input
            value={body}
            onChangeText={setBody}
            placeholder="Write the notice…"
            multiline
            style={{ minHeight: 120, textAlignVertical: 'top' }}
          />
        </Field>
        <View style={{
          flexDirection: 'row', alignItems: 'center', gap: space.s3,
          borderWidth: 1, borderColor: colors.borderSoft, borderRadius: 12,
          backgroundColor: colors.canvas1, padding: space.s3,
        }}>
          <View style={{ flex: 1 }}>
            <AppText variant="bodyMd" weight="600">Require acknowledgement</AppText>
            <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>Residents must tap to confirm they&apos;ve read it</AppText>
          </View>
          <Toggle on={requireAck} onChange={setRequireAck} label="Require acknowledgement" />
        </View>
        {notice ? <InfoNote icon="info" tint={colors.danger500}>{notice}</InfoNote> : null}
      </Screen>
      <BottomBar>
        <Button title="Publish notice" size="lg" disabled={!canPublish} loading={saving} onPress={() => { void publish(); }} />
      </BottomBar>
    </AdminScreenShell>
  );
}
